<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import ExerciseMCQ from "~/features/computer/components/ExerciseMCQ.vue";
import { chapterHubByUnit } from "~/features/computer/data/chapter-hubs";
import { buildComputerCollectionSchema, buildComputerNamedItemList } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

const props = defineProps<{
  unit: Exclude<ComputerMcqUnit, "python">;
}>();

const chapter = computerMcqChapterByUnit[props.unit];
const config = chapterHubByUnit[props.unit];
const faqs = [
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke MCQs kahan milte hain?`,
    answer: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke MCQs isi page aur full chapter MCQs page par answers ke sath milte hain.`,
  },
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke important MCQs kaise prepare karein?`,
    answer: `Pehle is page ke preview MCQs revise karein, phir full chapter MCQs aur exercise page kholo taake repeat concepts clear ho jayen.`,
  },
  {
    question: `Kya Chapter ${config.chapterNumber} ${chapter.shortTitle} ke MCQs exam me aate hain?`,
    answer: `Haan, ${chapter.shortTitle} chapter se objective questions aate hain. Is liye chapter-wise MCQ practice paper preparation ka important hissa hai.`,
  },
];
const relatedItems = [
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle}`, path: chapter.routes.chapterTo },
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Exercise`, path: chapter.routes.exerciseTo },
  { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
  { name: "Important Questions", path: "/11th/computer/important-questions/" },
];

usePageSeo({
  title: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs | Ustaad`,
  description: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs, solved objective questions aur quick revision flow.`,
  keywords: chapter.seo.keywords,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: `Ch ${config.chapterNumber} ${chapter.shortTitle}`, item: chapter.routes.chapterTo },
    { name: "MCQs", item: chapter.routes.mcqTo },
  ],
  schemaType: "CollectionPage",
  schemaProperties: {
    ...buildComputerCollectionSchema({
      about: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs`,
      items: relatedItems,
    }),
    mainEntity: buildComputerNamedItemList(chapter.questions.map((question) => question.question)),
  },
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter {{ config.chapterNumber }} {{ chapter.shortTitle }} MCQs</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">{{ chapter.description }}</p>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <ExerciseMCQ
          v-for="question in chapter.questions.slice(0, 5)"
          :key="question.id"
          :question="question"
        />
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
        <div class="grid gap-3">
          <NuxtLink :to="chapter.routes.exerciseTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Quick Exercise</NuxtLink>
          <NuxtLink to="/11th/computer/pairing-scheme/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Pairing Scheme</NuxtLink>
          <NuxtLink to="/11th/computer/important-questions/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Important Questions</NuxtLink>
          <NuxtLink :to="chapter.routes.chapterTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Back to Chapter</NuxtLink>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">FAQs</h2>
        <div class="mt-4 space-y-3">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
